import { useEffect, useMemo, useRef, useState } from 'react';
import '../App.css';
import { useApi } from '../hooks/useApi';
import { UnifiedTable } from './UnifiedTable';
import { fmtDate } from '../utils/ruDates';
import { matchesSearch } from '../utils/search';

const METRIC_DEFS = [
    { key: 'revenue', label: 'Заказано на сумму', type: 'number', group: 'sales' },
    { key: 'ordered_units', label: 'Заказано товаров', type: 'number', group: 'sales' },
    { key: 'average_check', label: 'Средний чек', type: 'number', group: 'sales' },
    { key: 'hits_view_search', label: 'Показы в поиске', type: 'number', group: 'shows' },
    { key: 'hits_view_pdp', label: 'Показы в карточке', type: 'number', group: 'shows' },
    { key: 'hits_view', label: 'Показы всего', type: 'number', group: 'shows' },
    { key: 'hits_tocart_search', label: 'В корзину из поиска', type: 'number', group: 'shows' },
    { key: 'hits_tocart_pdp', label: 'В корзину из карточки', type: 'number', group: 'shows' },
    { key: 'hits_tocart', label: 'В корзину всего', type: 'number', group: 'shows' },
    { key: 'session_view_search', label: 'Сессии поиска', type: 'number', group: 'shows' },
    { key: 'session_view_pdp', label: 'Сессии карточки', type: 'number', group: 'shows' },
    { key: 'session_view', label: 'Сессии всего', type: 'number', group: 'shows' },
    { key: 'conv_tocart_search', label: 'Конверсия в корзину (поиск) %', type: 'percent', group: 'conversion' },
    { key: 'conv_tocart_pdp', label: 'Конверсия в корзину (карточка) %', type: 'percent', group: 'conversion' },
    { key: 'conv_tocart', label: 'Конверсия в корзину %', type: 'percent', group: 'conversion' },
];

const GROUP_LABELS = {
    sales: 'Продажи',
    shows: 'Показы',
    conversion: 'Конверсии',
};

function OzonStatistics({ accountId, accountName, openLightbox, projectMode = false, projectCabinets = [] }) {
    const { authFetch, API_BASE_URL } = useApi();

    const accountIds = projectMode && projectCabinets.length > 0
        ? projectCabinets.map((c) => c.id)
        : accountId ? [accountId] : [];

    const accountNameMap = useMemo(() => {
        const map = {};
        if (projectMode && projectCabinets.length > 0) {
            for (const cab of projectCabinets) map[cab.id] = cab.account_name || cab.name || cab.id;
        } else if (accountId) {
            map[accountId] = accountName;
        }
        return map;
    }, [projectMode, projectCabinets, accountId, accountName]);

    const [periodFrom, setPeriodFrom] = useState('');
    const [periodTo, setPeriodTo] = useState('');
    const [groupBy, setGroupBy] = useState('sku'); // 'sku' | 'model'
    const [rawRows, setRawRows] = useState([]);
    const [productData, setProductData] = useState({});
    const [loading, setLoading] = useState(false);
    // null — ещё не грузили; иначе — последний день с данными по каждому кабинету (для пустого результата)
    const [lastDataDates, setLastDataDates] = useState(null);
    const [search, setSearch] = useState('');
    const [hideZeros, setHideZeros] = useState(true);
    const [showShopColumn, setShowShopColumn] = useState(false);
    const [sortField, setSortField] = useState('revenue');
    const [sortDir, setSortDir] = useState('desc');
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [hiddenGroups, setHiddenGroups] = useState([]);
    // Пагинацию, сортировку и видимость колонок держит UnifiedTable.

    const visibleMetrics = useMemo(
        () => METRIC_DEFS.filter((m) => !hiddenColumns.includes(m.key) && !hiddenGroups.includes(m.group)),
        [hiddenColumns, hiddenGroups]
    );
    const accountIdsKey = useMemo(
        () => [...accountIds].map(String).sort().join(',') || 'none',
        [projectMode, projectCabinets, accountId]
    );
    const storageKey = `ozon_statistics_state_${projectMode ? 'project' : 'account'}_${accountIdsKey}`;

    const applyPreset = (days) => {
        const to = new Date();
        to.setDate(to.getDate() - 1);
        const from = new Date(to);
        from.setDate(from.getDate() - (days - 1));
        const d = (x) => x.toISOString().split('T')[0];
        setPeriodFrom(d(from));
        setPeriodTo(d(to));
    };

    const toggleColumn = (key) => {
        setHiddenColumns((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    };

    const toggleGroup = (group) => {
        setHiddenGroups((prev) => (prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]));
    };

    useEffect(() => {
        try {
            const rawState = localStorage.getItem(storageKey);
            if (!rawState) return;
            const saved = JSON.parse(rawState);
            setPeriodFrom(saved.periodFrom || '');
            setPeriodTo(saved.periodTo || '');
            setGroupBy(saved.groupBy === 'model' ? 'model' : 'sku');
            // Снимок из старой версии мог не иметь нужных полей — такая строка роняла отрисовку всей вкладки.
            setRawRows(Array.isArray(saved.rawRows)
                ? saved.rawRows
                    .filter((row) => row && typeof row === 'object')
                    .map((row) => ({
                        ...row,
                        sourceAccountIds: Array.isArray(row.sourceAccountIds) ? row.sourceAccountIds : [],
                        metrics: row.metrics || {},
                    }))
                : []);
            setProductData(saved.productData || {});
            setSearch(saved.search || '');
            setHideZeros(saved.hideZeros ?? true);
            setShowShopColumn(saved.showShopColumn ?? false);
            setSortField(saved.sortField || 'revenue');
            setSortDir(saved.sortDir || 'desc');
            setHiddenColumns(Array.isArray(saved.hiddenColumns) ? saved.hiddenColumns : []);
            setHiddenGroups(Array.isArray(saved.hiddenGroups) ? saved.hiddenGroups : []);
        } catch (_) {
            // ignore broken local state
        }
    }, [storageKey]);

    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify({
                periodFrom,
                periodTo,
                groupBy,
                rawRows,
                productData,
                search,
                hideZeros,
                showShopColumn,
                sortField,
                sortDir,
                hiddenColumns,
                hiddenGroups,
            }));
        } catch (_) {
            // ignore storage errors
        }
    }, [
        storageKey,
        periodFrom,
        periodTo,
        groupBy,
        rawRows,
        productData,
        search,
        hideZeros,
        showShopColumn,
        sortField,
        sortDir,
        hiddenColumns,
        hiddenGroups,
    ]);

    const fetchProductData = async () => {
        const responses = await Promise.all(
            accountIds.map((id) => authFetch(`${API_BASE_URL}/api/v1/ozon/${id}/sku-mapping`).catch((e) => {
                console.warn('sku-mapping fetch failed', id, e);
                return {};
            }))
        );
        const merged = {};
        for (const part of responses) Object.assign(merged, part || {});
        setProductData(merged);
    };


    const loadData = async () => {
        if (!periodFrom || !periodTo) return alert('Укажите период');
        if (accountIds.length === 0) return alert('Нет кабинетов для загрузки');

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        try {
            const payload = {
                period1_from: new Date(periodFrom).toISOString(),
                period1_to: new Date(periodTo).toISOString(),
                // Для "Статистики" используем только period1. period2 дублируем, чтобы API compare не брал авто-предыдущий период.
                period2_from: new Date(periodFrom).toISOString(),
                period2_to: new Date(periodTo).toISOString(),
                group_by: groupBy,
                live: true, // «Статистика» качает данные из Ozon в момент запроса
            };

            const responses = await Promise.all(
                accountIds.map(async (id) => {
                    const r = await authFetch(`${API_BASE_URL}/api/v1/ozon-analytics/${id}/compare`, {
                        method: 'POST',
                        body: JSON.stringify(payload),
                        signal: controller.signal,
                    });
                    return { id, r };
                })
            );

            setLastDataDates(responses.map(({ r }) => r.last_data_date).filter(Boolean));

            const skuMap = new Map();
            for (const { id, r } of responses) {
                for (const item of r.by_sku || []) {
                    const sku = String(item.sku || '');
                    const row = skuMap.get(sku) || {
                        sku,
                        name: item.name || sku,
                        skuCount: 0,
                        sourceAccountIds: [],
                        metrics: {},
                    };
                    if (!row.sourceAccountIds.includes(id)) row.sourceAccountIds.push(id);
                    row.skuCount += Number(item.sku_count || 0);
                    for (const def of METRIC_DEFS) {
                        if (def.key === 'average_check') continue;
                        row.metrics[def.key] = (row.metrics[def.key] || 0) + Number(item.metrics?.[def.key]?.period1 || 0);
                    }
                    skuMap.set(sku, row);
                }
            }

            const rows = Array.from(skuMap.values()).map((row) => {
                const rev = Number(row.metrics.revenue || 0);
                const units = Number(row.metrics.ordered_units || 0);
                row.metrics.average_check = units > 0 ? rev / units : 0;
                return row;
            });

            setRawRows(rows);
            if (groupBy === 'model') {
                // "sku" is a model name here, not a real SKU — no per-product
                // offer_id/image to look up.
                setProductData({});
            } else {
                await fetchProductData();
            }
        } catch (e) {
            if (controller.signal.aborted) return; // запрос заменён новым или страница закрыта
            alert(`Ошибка загрузки: ${e.message}`);
            setRawRows([]);
        } finally {
            if (abortRef.current === controller) {
                abortRef.current = null;
                setLoading(false);
            }
        }
    };

    // Сохранённый в localStorage снимок показывается как есть. Ozon дёргается только по кнопке
    // «Загрузить»: тихий автозапрос при заходе занимал очередь метода (лимит 1 запрос в минуту).
    // Незавершённый запрос прерывается при новой загрузке и при уходе со страницы, иначе он
    // продолжал бы держать очередь на сервере.
    const abortRef = useRef(null);
    useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

    const getMetric = (row, key) => Number(row.metrics?.[key] || 0);

    const rows = useMemo(() => {
        let out = [...rawRows];

        if (search.trim()) {
            out = out.filter((row) => matchesSearch([row, productData[row.sku]], search));
        }

        if (hideZeros) {
            out = out.filter((row) => visibleMetrics.some((m) => getMetric(row, m.key) !== 0));
        }

        out.sort((a, b) => {
            let av;
            let bv;
            if (sortField === 'sku' || sortField === 'name') {
                av = a[sortField] || '';
                bv = b[sortField] || '';
            } else if (sortField === 'offer_id') {
                av = productData[a.sku]?.offer_id || '';
                bv = productData[b.sku]?.offer_id || '';
            } else if (sortField === 'skuCount') {
                av = a.skuCount || 0;
                bv = b.skuCount || 0;
            } else {
                av = getMetric(a, sortField);
                bv = getMetric(b, sortField);
            }
            if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(String(bv || ''), 'ru') : String(bv || '').localeCompare(av, 'ru');
            const an = Number(av);
            const bn = Number(bv);
            const avNum = Number.isFinite(an) ? an : 0;
            const bvNum = Number.isFinite(bn) ? bn : 0;
            if (avNum === bvNum) {
                return String(a.sku || '').localeCompare(String(b.sku || ''), 'ru');
            }
            return sortDir === 'asc' ? avNum - bvNum : bvNum - avNum;
        });
        return out;
    }, [rawRows, search, hideZeros, visibleMetrics, sortField, sortDir, productData]);

    const totals = useMemo(() => {
        if (rows.length === 0) return null;
        const result = {};
        for (const def of METRIC_DEFS) {
            let sum = 0;
            for (const row of rows) sum += getMetric(row, def.key);
            result[def.key] = sum;
        }
        if (result.ordered_units > 0) result.average_check = result.revenue / result.ordered_units;
        return result;
    }, [rows]);

    const handleSortChange = (key, dir) => {
        setSortField(key);
        setSortDir(dir);
    };

    const n = (v) => (v === null || v === undefined ? '-' : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(v));
    const p = (v) => (v === null || v === undefined ? '-' : `${n(v)}%`);
    const val = (v, type) => (type === 'percent' ? p(v) : n(v));
    const d = (x) => (x ? fmtDate(x) : '-');

    // Колонки для UnifiedTable. Метрики скрытых групп выкидываются совсем, а поштучно
    // скрытые колонки отданы в hiddenColumns — чипсы "Колонки" и меню таблицы
    // работают с одним и тем же состоянием.
    const tableColumns = useMemo(() => {
        const cols = [];
        if (showShopColumn) {
            cols.push({ key: 'shop', header: 'Магазин', width: 150, sticky: true });
        }
        if (groupBy === 'model') {
            cols.push({ key: 'name', header: 'Модель', width: 280, sticky: true });
            cols.push({ key: 'skuCount', header: 'Товаров', type: 'number', align: 'right', width: 100 });
        } else {
            cols.push({ key: 'offer_id', header: 'Артикул', width: 150, sticky: true });
            cols.push({ key: 'image', header: 'Фото', type: 'image', imageSize: 40, width: 70, sortable: false, exportable: false });
            cols.push({
                key: 'sku',
                header: 'SKU',
                width: 130,
                render: (value) => (
                    <a className="sku-link" href={`https://www.ozon.ru/product/${value}`} target="_blank" rel="noreferrer">{value}</a>
                ),
            });
            cols.push({ key: 'name', header: 'Название', width: 280 });
        }
        for (const m of METRIC_DEFS) {
            if (hiddenGroups.includes(m.group)) continue;
            cols.push({
                key: m.key,
                header: m.label,
                type: 'number',
                align: 'right',
                width: 150,
                format: (v) => val(v, m.type),
            });
        }
        return cols;
        // eslint-disable-next-line
    }, [showShopColumn, groupBy, hiddenGroups]);

    const tableData = useMemo(
        () => rows.map((row) => {
            const pd = productData[row.sku] || {};
            const flat = {
                sku: row.sku,
                name: row.name,
                skuCount: row.skuCount || 0,
                offer_id: pd.offer_id || '',
                image: pd.image || '',
                shop: (row.sourceAccountIds || []).map((id) => accountNameMap[id] || id).join(', ') || accountName || '-',
            };
            for (const m of METRIC_DEFS) flat[m.key] = getMetric(row, m.key);
            return flat;
        }),
        [rows, productData, accountNameMap, accountName]
    );

    return (
        <div className="ozon-analytics-panel">
            <div className="analytics-controls">
                <div className="analytics-periods">
                    <div className="period-row">
                        <label className="period-label">Период:</label>
                        <input type="date" className="period-input" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
                        <span className="period-separator">—</span>
                        <input type="date" className="period-input" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
                    </div>
                </div>
                <div className="analytics-presets">
                    <span className="presets-label">Быстрый выбор:</span>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => applyPreset(7)}>Неделя</button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => applyPreset(30)}>Месяц</button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => applyPreset(90)}>Квартал</button>
                </div>
                <div className="analytics-group-toggle">
                    <button
                        type="button"
                        className={`btn btn-sm ${groupBy === 'sku' ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => setGroupBy('sku')}
                    >
                        По артикулам
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${groupBy === 'model' ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => setGroupBy('model')}
                    >
                        По моделям
                    </button>
                </div>
                <div className="analytics-actions">
                    <button type="button" className="btn btn-primary" onClick={loadData} disabled={loading || accountIds.length === 0}>
                        {loading ? 'Загрузка...' : 'Загрузить'}
                    </button>
                </div>
            </div>

            {loading && (
                <div className="analytics-empty">
                    <p>
                        У Ozon действует ограничение на этот метод: не больше 1 запроса в минуту, поэтому загрузка может быть долгой.
                        Воспользуйтесь вкладкой «Логи», чтобы не ждать.
                    </p>
                </div>
            )}

            {!loading && lastDataDates !== null && rawRows.length === 0 && (
                <div className="analytics-empty">
                    <p>За выбранный период данных нет.</p>
                    <p>
                        {lastDataDates.length > 0
                            ? `Последний день со статистикой в базе: ${fmtDate(lastDataDates.sort()[lastDataDates.length - 1])}. Выберите период до этой даты.`
                            : 'Статистика по этому кабинету ещё не загружена.'}
                    </p>
                </div>
            )}

            {rawRows.length > 0 && (
                <div className="analytics-filters">
                    <div className="filter-main-row">
                        <div className="filter-toggles">
                            <button type="button" className={`filter-toggle ${hideZeros ? 'active' : ''}`} onClick={() => setHideZeros((v) => !v)}>
                                Скрыть нулевые
                            </button>
                            <button type="button" className={`filter-toggle ${showShopColumn ? 'active' : ''}`} onClick={() => setShowShopColumn((v) => !v)}>
                                Показать магазин
                            </button>
                        </div>
                    </div>
                    <div className="filter-vis-row">
                        <span className="filter-vis-label">Группы:</span>
                        <div className="filter-chips-row">
                            {Object.keys(GROUP_LABELS).map((group) => (
                                <button key={group} type="button" className={`vis-chip ${!hiddenGroups.includes(group) ? 'vis-chip--on' : ''}`} onClick={() => toggleGroup(group)}>
                                    {GROUP_LABELS[group]}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="filter-vis-row">
                        <span className="filter-vis-label">Колонки:</span>
                        <div className="filter-chips-row">
                            {METRIC_DEFS.map((m) => (
                                <button key={m.key} type="button" className={`vis-chip ${!hiddenColumns.includes(m.key) ? 'vis-chip--on' : ''}`} onClick={() => toggleColumn(m.key)}>
                                    {m.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {rawRows.length > 0 && totals && (
                <>
                    <div className="analytics-summary">
                        <h3>Общая статистика</h3>
                        <div className="summary-table-wrapper">
                            <table className="summary-table">
                                <thead>
                                    <tr>
                                        <th>Период</th>
                                        {visibleMetrics.map((m) => <th key={m.key} className="text-right">{m.label}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{d(periodFrom)} — {d(periodTo)}</td>
                                        {visibleMetrics.map((m) => <td key={m.key} className="text-right">{val(totals[m.key], m.type)}</td>)}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="analytics-detail">
                        <h3>{groupBy === 'model' ? `Детализация по моделям (${rows.length})` : `Детализация по товарам (${rows.length})`}</h3>
                        <UnifiedTable
                            tableId={`ozon-statistics-${groupBy}`}
                            columns={tableColumns}
                            data={tableData}
                            sortKey={sortField}
                            sortDir={sortDir}
                            onSortChange={handleSortChange}
                            search={search}
                            onSearchChange={setSearch}
                            hiddenColumns={hiddenColumns}
                            onHiddenColumnsChange={setHiddenColumns}
                            onImageClick={openLightbox ? (url) => openLightbox([url], 0) : undefined}
                            getRowKey={(row) => row.sku}
                            exportFilename={`ozon-statistics-${periodFrom}_${periodTo}`}
                            fullscreenEnabled
                            loading={loading}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

export default OzonStatistics;
